'use client';

import { useEffect, useState } from 'react';

import {
  type TableSettings,
  tableSettingsStore,
} from '@campaign-manager/core/lib/storage/tableSettingsStore';

const COMPACT_VIEW_TOGGLE_EVENT = 'compact-view-toggle';

/**
 * Hook to read and toggle the compact view setting for a specific table.
 * Compact view merges multiple columns into compound columns for a denser layout.
 * The setting is persisted per-table in IndexedDB via the table settings store.
 */
export function useCompactView(tableId: string): [boolean, () => void] {
  const [compactView, setCompactView] = useState(false);

  useEffect(() => {
    const loadSetting = async () => {
      try {
        const settings = await tableSettingsStore.getSettings(tableId);
        if (settings?.compactView) {
          setCompactView(true);
        }
      } catch (error) {
        console.error('Failed to load compact view setting:', error);
      }
    };

    loadSetting();

    // Listen for same-tab toggle events from other hook instances
    const handleToggle = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.tableId === tableId) {
        setCompactView(detail.compactView);
      }
    };

    window.addEventListener(COMPACT_VIEW_TOGGLE_EVENT, handleToggle);
    return () => window.removeEventListener(COMPACT_VIEW_TOGGLE_EVENT, handleToggle);
  }, [tableId]);

  const toggle = () => {
    const newValue = !compactView;
    setCompactView(newValue);

    // Persist to IndexedDB
    tableSettingsStore.getSettings(tableId).then((existing) => {
      const settings: TableSettings = {
        ...(existing || { tableId }),
        tableId,
        compactView: newValue,
      };
      tableSettingsStore.saveSettings(settings);
    });

    // Notify other hook instances in the same tab
    window.dispatchEvent(
      new CustomEvent(COMPACT_VIEW_TOGGLE_EVENT, {
        detail: { tableId, compactView: newValue },
      }),
    );
  };

  return [compactView, toggle];
}
